import { useDeferredValue, useMemo } from 'react';
import { Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { OrgUnitAssortment } from '@retail/mgpris/config-types';
import {
  ExportToExcelButton,
  FilterChip,
  FilterChipContainer,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
} from '@retail/components';
import { OrgUnitType } from '@retail/mgpris/config-types';
import {
  AssortmentPricesFilters,
  AssortmentPricesItemsTable,
} from '@retail/my-assortment/components';
import { useIncludeVatMyAssortment } from '@retail/my-assortment/context';
import {
  AssortmentExportColumn,
  assortmentViewExportColumns,
  useExportAssortmentItemsToExcel,
  useFetchAssortmentHierarchyFilterOptions,
  useFetchAssortmentPrices,
} from '@retail/my-assortment/data-access';
import {
  AssortmentPrices,
  AssortmentView,
  HierarchyFilterContext,
  isMainSupplierItem,
} from '@retail/my-assortment/types';
import { BaseAssortmentFilters, baseAssortmentFilters } from '@retail/my-assortment/utils';
import { TotalItemsLoader } from '@retail/products/components';
import {
  AssortmentItemIdentifiersFilterDialog,
  MgItemContainer,
  MgItemDialogContainer,
} from '@retail/products/containers';
import { useFilters } from '@retail/products/hooks';
import { createItemIdentifiersPredicate } from '@retail/products/utils';
import { brandLogo } from '@retail/utils';
import { useDisclosure, useSelectedSearchParamState } from '@shared/hooks';
import { Filter, filterList } from '@shared/utils';
import { CustomerAssortmentSelect } from '@retail/customer-discount/components';

interface Props {
  customerAssortment: OrgUnitAssortment;
  selectAssortment: (assortmentId: number | null) => void;
  selectedCustomerAssortment: OrgUnitAssortment | null;
  customerAssortments: OrgUnitAssortment[];
}

const getItemId = ({ mgSupplierItemId }: AssortmentPrices) => mgSupplierItemId;

export function CustomerAssortmentPrices({
  customerAssortment,
  selectAssortment,
  selectedCustomerAssortment,
  customerAssortments,
}: Props) {
  const { appTenant } = useSelectedOrgUnit();
  const { userCanRead } = usePermissionsForCurrentUser();
  const { selectedBrand } = useUserBrands();
  const showFilters = useDisclosure(true);
  const itemIdentifiersFilterDialog = useDisclosure(false);
  const { includeVat, toggleIncludeVat } = useIncludeVatMyAssortment();
  const { filters, setFilters } = useFilters<BaseAssortmentFilters>(baseAssortmentFilters);

  const { data: filterContextDTO } = useFetchAssortmentHierarchyFilterOptions({
    assortmentId: customerAssortment.assortmentId,
    supplierNumber: filters.supplierNumber ? Number(filters.supplierNumber) : undefined,
    suspense: false,
  });

  const filterContext = useMemo(
    () => (filterContextDTO ? HierarchyFilterContext.fromDto(filterContextDTO) : undefined),
    [filterContextDTO]
  );

  const deferredFilters = useDeferredValue(filters);

  const itemIdentifiersPredicate = useMemo(
    () =>
      deferredFilters.itemIdentifiers
        ? createItemIdentifiersPredicate(deferredFilters.itemIdentifiers.identifiers)
        : undefined,
    [deferredFilters.itemIdentifiers]
  );

  const { data: assortmentWithCountDTO, isFetching: isFetchingItems } = useFetchAssortmentPrices({
    assortmentId: filters.forApproval
      ? customerAssortment.draftAssortmentId
      : customerAssortment.assortmentId,
    supplierNumber: filters.supplierNumber ? Number(filters.supplierNumber) : undefined,
    productGroupNumbers: filters.productGroupNumbers,
    discountGroupCodes: filters.discountGroupCodes,
    query: filters.searchQuery ?? undefined,
    assortmentTags: filters.subAssortmentIds,
    withAgreementPrices:
      filters.purchasePrice === 'UNSPECIFIED' ? undefined : filters.purchasePrice === 'YES',
    suspense: false,
  });
  const assortmentPrices = useMemo(
    () =>
      assortmentWithCountDTO?.items.map((dto) =>
        AssortmentPrices.fromDTO(dto, {
          name: selectedBrand.name,
          id: selectedBrand.brandId,
          imgUrl: brandLogo(selectedBrand.brandId).logo,
        })
      ) ?? [],
    [assortmentWithCountDTO?.items, selectedBrand.brandId, selectedBrand.name]
  );
  const items = useMemo(() => {
    const frontendFilters: Filter<AssortmentPrices>[] = [
      {
        active: deferredFilters.forApproval,
        predicate: (item) => item.inDraft,
      },
      {
        active: deferredFilters.activeItems !== 'UNSPECIFIED',
        predicate: (item) => {
          const isItemActive = item.isActiveBasedOnDiscontinuationStatus();
          return deferredFilters.activeItems === 'YES' ? isItemActive : !isItemActive;
        },
      },
      {
        active: deferredFilters.inStock !== 'UNSPECIFIED',
        predicate: (item) =>
          deferredFilters.inStock === 'NO'
            ? !item.isStocked
            : item.isStocked || item.isStocked === undefined,
      },
      {
        active: !!itemIdentifiersPredicate,
        predicate: (item) => !!itemIdentifiersPredicate?.(item),
      },
      {
        active: appTenant.hasMainSupplier && deferredFilters.fromMainSupplier,
        predicate: isMainSupplierItem,
      },
    ];
    return filterList({ filters: frontendFilters, initList: assortmentPrices });
  }, [
    deferredFilters.forApproval,
    deferredFilters.activeItems,
    deferredFilters.inStock,
    deferredFilters.fromMainSupplier,
    itemIdentifiersPredicate,
    appTenant.hasMainSupplier,
    assortmentPrices,
  ]);

  const { isLoading: isLoadingExport, exportToExcel } = useExportAssortmentItemsToExcel({
    fileName: `${customerAssortment.name} assortment ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
    items,
    columns: Object.entries(assortmentViewExportColumns)
      .filter(([_, { orgUnits }]) => orgUnits.includes(OrgUnitType.WHOLESALE_PRICE_LIST))
      .map(([col]) => col as AssortmentExportColumn),
  });

  const [selectedProductCardItem, , setSelectedProductCardItemId] = useSelectedSearchParamState({
    options: items,
    searchParamKey: 'mgItem',
    getOptionId: getItemId,
  });

  const totalItems = assortmentWithCountDTO?.totalNumberOfItems ?? 0;
  const selectedView = filters.assortmentView as AssortmentView;

  return (
    <Stack flexGrow={1} overflow="hidden">
      <ToggleFiltersToolbar sx={{ justifyContent: 'space-between', mb: 1 }}>
        <Stack flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">
          {customerAssortments.length > 1 && (
            <FilterChip name={customerAssortment.name} onDelete={() => selectAssortment(null)} />
          )}
          <FilterChipContainer
            allowedKeys={baseAssortmentFilters.filter(
              (f) =>
                f !== 'assortmentView' && (appTenant.hasMainSupplier || f !== 'fromMainSupplier')
            )}
            filterOptions={{ ...filterContext, subAssortments: filterContext?.assortmentTags }}
          />
        </Stack>
        <Stack flexDirection="row" alignItems="flex-end" gap={1}>
          <TotalItemsLoader
            totalItems={totalItems}
            itemsShowing={items.length}
            isFetching={isFetchingItems}
          />
          <ExportToExcelButton
            disabled={totalItems === 0}
            loading={isLoadingExport}
            onClick={exportToExcel}
          />
          <ToggleFiltersButton
            showFilters={showFilters.isOpen}
            toggleFilters={showFilters.onToggle}
          />
        </Stack>
      </ToggleFiltersToolbar>
      <Stack flexGrow={1} flexDirection="row" overflow="hidden">
        <Paper sx={{ display: 'flex', flexGrow: 1, overflow: 'auto' }}>
          <AssortmentPricesItemsTable
            items={items}
            emptyAssortment={!!assortmentWithCountDTO && totalItems === 0}
            selectedItem={selectedProductCardItem}
            loadingFetchItems={isFetchingItems}
            includeVat={includeVat}
            selectedView={selectedView}
            isDeleteEnabled={false}
            isPriceCalculationEnabled={userCanRead('PRICE_CALCULATION')}
            isPriceSuggestionEnabled={userCanRead('PRICE_CALCULATION__PRICE_SUGGESTIONS')}
            orgUnitType={OrgUnitType.WHOLESALE_PRICE_LIST}
            externalItemSource={appTenant.externalItemSource}
            showMainSupplierName={appTenant.hasMainSupplier}
            viewProductCard={setSelectedProductCardItemId}
          />
        </Paper>
        <AssortmentPricesFilters
          filterOptions={filterContext}
          includeVat={includeVat}
          showFilters={showFilters.isOpen}
          canReadCompetitorPrices={false}
          toggleIncludeVat={toggleIncludeVat}
          onOpenItemIdentifiersFilter={itemIdentifiersFilterDialog.onOpen}
          showFromMainSupplier={appTenant.hasMainSupplier}
          header={
            <CustomerAssortmentSelect
              customerAssortments={customerAssortments}
              selectedAssortmentId={selectedCustomerAssortment?.assortmentId ?? null}
              selectAssortment={selectAssortment}
            />
          }
        />
      </Stack>

      {itemIdentifiersFilterDialog.isOpen && (
        <AssortmentItemIdentifiersFilterDialog
          assortmentId={customerAssortment.assortmentId}
          useFetchItems={useFetchAssortmentPrices}
          onApply={(itemIdentifiers) => {
            setFilters({ itemIdentifiers });
            itemIdentifiersFilterDialog.onClose();
          }}
          onClose={itemIdentifiersFilterDialog.onClose}
        />
      )}

      {selectedProductCardItem && (
        <MgItemDialogContainer
          open
          onClose={() => setSelectedProductCardItemId(null)}
          itemNumber={selectedProductCardItem.mgItemNumber}
        >
          {({ mgItem }) => (
            <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
          )}
        </MgItemDialogContainer>
      )}
    </Stack>
  );
}
